import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import SuccessIndicator from './components/SuccessIndicator';
import ResendButton from './components/ResendButton';
import SupportContact from './components/SupportContact';
import VerificationInstructions from './components/VerificationInstructions';
import { logger } from '../../utils/logger';

const EmailVerificationConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { resendVerificationEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');

  // Get email from navigation state
  useEffect(() => {
    const emailFromState = location?.state?.email;
    if (emailFromState) {
      setEmail(emailFromState);
      logger?.info('Email verification confirmation loaded', { email: emailFromState });
    } else {
      // If no email in state, redirect to sign-up
      logger?.warn('No email found in state, redirecting to sign-up');
      navigate('/sign-up');
    }
  }, [location, navigate]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendEmail = async () => {
    if (resendCooldown > 0 || !email) return;

    setResendLoading(true);
    setResendError('');
    setResendSuccess(false);

    try {
      const result = await resendVerificationEmail(email);

      if (result?.error) {
        setResendError(result?.error?.message || 'Failed to resend verification email. Please try again.');
        logger?.error('Resend verification email failed', { email, error: result?.error });
      } else {
        setResendSuccess(true);
        setResendCooldown(60); // 60 second cooldown
        logger?.info('Verification email resent successfully', { email });
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setResendSuccess(false);
        }, 5000);
      }
    } catch (error) {
      setResendError('Network error. Please check your connection and try again.');
      logger?.error('Resend verification email error', { email, error });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Check Your Email - MyBodyToday</title>
        <meta name="description" content="Please check your email to verify your account and complete registration" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            {/* Success Indicator */}
            <SuccessIndicator />

            {/* Main Message */}
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Check Your Email
              </h1>
              <p className="text-lg text-gray-600 mb-2">
                We've sent a verification link to:
              </p>
              <p className="text-xl font-semibold text-blue-600 mb-6">
                {email}
              </p>
            </div>

            {/* Verification Instructions */}
            <VerificationInstructions />

            {/* Resend Button */}
            <ResendButton
              onResend={handleResendEmail}
              loading={resendLoading}
              cooldown={resendCooldown}
              success={resendSuccess}
              error={resendError}
            />

            {/* Support Contact */}
            <SupportContact />

            {/* Back to Sign In */}
            <div className="text-center pt-6 border-t border-gray-200">
              <p className="text-gray-600 mb-3">
                Already verified your email?
              </p>
              <Link to="/sign-in">
                <Button
                  variant="outline"
                  className="w-full md:w-auto"
                >
                  <Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
                  Go to Sign In
                </Button>
              </Link>
            </div>
          </div>

          {/* Additional Help */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Having trouble? Check your spam folder or{' '}
              <a
                href="mailto:support@mybodytoday1.com"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                contact support
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailVerificationConfirmation;